import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE || "";

function getMunicipalityName(feature) {
  const p = feature?.properties || {};
  return p.municipality || p.MUN_NAME || p.name || p.NAME || "";
}

function normalizeName(name) {
  return (name || "").trim().toLowerCase();
}

function getFeatureCenter(feature) {
  try {
    const layer = L.geoJSON(feature);
    const bounds = layer.getBounds();
    return bounds.getCenter();
  } catch {
    return null;
  }
}

function municipalityColor(name) {
  const key = normalizeName(name);

  const colors = {
    "vaughan": "#F6D6AD",
    "markham": "#A7D5C5",
    "richmond hill": "#D7C6F3",
    "newmarket": "#F5B7B1",
    "aurora": "#BFE3E0",
    "georgina": "#FFD9C7",
    "east gwillimbury": "#C7DDF5",
    "whitchurch-stouffville": "#E6D5F7",
    "king": "#DDECC7",
  };

  return colors[key] || "#ECECEC";
}

export default function MunicipalityMap() {
  const [geojson, setGeojson] = useState(null);
  const [summary, setSummary] = useState([]);
  const [geojsonError, setGeojsonError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const navigate = useNavigate();
  const geojsonRef = useRef(null);

  useEffect(() => {
    fetch("/Municipal_Boundary.geojson")
      .then((r) => {
        if (!r.ok) throw new Error(`GeoJSON HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => setGeojson(d))
      .catch((e) => {
        console.error("GeoJSON load error:", e);
        setGeojsonError(e.message || String(e));
      });

    fetch(`${API_BASE}/api/analysis/dashboard/municipality-summary/`)
      .then((r) => {
        if (!r.ok) throw new Error(`Summary HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => setSummary(d))
      .catch((e) => {
        console.error("Summary load error:", e);
        setSummaryError(e.message || String(e));
      });
  }, []);

  const countMap = useMemo(() => {
    const m = {};
    for (const row of summary) {
      m[normalizeName(row.municipality)] = row.total;
    }
    return m;
  }, [summary]);

  const enrichedGeojson = useMemo(() => {
    if (!geojson) return null;

    return {
      ...geojson,
      features: (geojson.features || []).map((f) => {
        const munName = getMunicipalityName(f);
        const total = countMap[normalizeName(munName)] || 0;
        return {
          ...f,
          properties: {
            ...f.properties,
            __municipality_name: munName,
            __total: total,
          },
        };
      }),
    };
  }, [geojson, countMap]);

  const style = (feature) => {
    const munName = feature.properties?.__municipality_name || "";
    return {
      color: "#6F7D86",
      weight: 2.2,
      opacity: 0.95,
      fillColor: municipalityColor(munName),
      fillOpacity: 0.72,
      lineJoin: "round",
    };
  };

  const onEachFeature = (feature, layer) => {
    const munName = feature.properties?.__municipality_name || "";
    const total = feature.properties?.__total || 0;

    layer.bindTooltip(
      `<strong>${munName}</strong><br/>Applications: ${total}`,
      { sticky: true }
    );

    layer.on({
      click: () => {
        navigate(`/dashboard?mun=${encodeURIComponent(munName)}`);
      },
      mouseover: (e) => {
        e.target.setStyle({
          weight: 3.5,
          color: "#46525A",
          opacity: 1,
          fillOpacity: 0.88,
        });
      },
      mouseout: (e) => {
        if (geojsonRef.current) {
          geojsonRef.current.resetStyle(e.target);
        }
      },
    });
  };

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 16 }}>York Region Municipality Overview</h2>

      {summaryError && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #FCD34D",
            background: "#FFFBEB",
            color: "#92400E",
            fontSize: 14,
          }}
        >
          Application counts could not be loaded ({summaryError}), so the numbers on the map may show 0.
          Please refresh the page to try again.
        </div>
      )}

      <div
        style={{
          position: "relative",
          height: "78vh",
          borderRadius: 14,
          overflow: "hidden",
          border: "1px solid #D9D9D9",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        }}
      >
        {!geojson && (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 1001, // above Leaflet's panes and controls
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.55)",
              pointerEvents: "none",
            }}
          >
            {geojsonError ? (
              <div
                role="alert"
                style={{
                  maxWidth: 420,
                  padding: "16px 20px",
                  borderRadius: 12,
                  border: "1px solid #FCA5A5",
                  background: "#FEF2F2",
                  color: "#B91C1C",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  textAlign: "center",
                }}
              >
                <strong>Could not load the municipality boundaries.</strong>
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  {geojsonError}. Please refresh the page to try again.
                </div>
              </div>
            ) : (
              <div
                role="status"
                style={{
                  padding: "14px 20px",
                  borderRadius: 12,
                  border: "1px solid #D9D9D9",
                  background: "#ffffff",
                  color: "#2F3E46",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  textAlign: "center",
                }}
              >
                <strong>Loading map…</strong>
                <div style={{ marginTop: 4, fontSize: 13, opacity: 0.75 }}>
                  This can take a while on slow connections.
                </div>
              </div>
            )}
          </div>
        )}

        <MapContainer
          center={[43.88, -79.44]}
          zoom={10}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {enrichedGeojson && (
            <>
              <GeoJSON
                data={enrichedGeojson}
                style={style}
                onEachFeature={onEachFeature}
                ref={geojsonRef}
              />

              {(enrichedGeojson.features || []).map((feature, idx) => {
                const center = getFeatureCenter(feature);
                if (!center) return null;

                const munName = feature.properties?.__municipality_name || "";
                const total = feature.properties?.__total || 0;

                const icon = L.divIcon({
                  className: "municipality-count-icon",
                  html: `
                    <div style="
                      background:#ffffff;
                      border:2px solid #5B6C76;
                      color:#2F3E46;
                      border-radius:999px;
                      min-width:38px;
                      height:38px;
                      display:flex;
                      align-items:center;
                      justify-content:center;
                      font-weight:700;
                      box-shadow:0 4px 12px rgba(0,0,0,0.16);
                      cursor:pointer;
                      padding:0 10px;
                      font-size:14px;
                    ">
                      ${total}
                    </div>
                  `,
                  iconSize: [38, 38],
                  iconAnchor: [19, 19],
                });

                return (
                  <Marker
                    key={idx}
                    position={center}
                    icon={icon}
                    eventHandlers={{
                      click: () => {
                        navigate(`/dashboard?mun=${encodeURIComponent(munName)}`);
                      },
                    }}
                  >
                    <Popup>
                      <strong>{munName}</strong>
                      <br />
                      Applications: {total}
                    </Popup>
                  </Marker>
                );
              })}
            </>
          )}
        </MapContainer>
      </div>

      <div style={{ marginTop: 12, opacity: 0.8 }}>
        Click on any area or number to open that municipality's dashboard.
      </div>
    </div>
  );
}